
/**
 * Get the API variable name from a form field
 */
export const getVariableNameFromField = (field?: any): string => {
  if (!field) return 'unknown_field';
  
  // Use variable_name if set, otherwise convert label to snake_case
  return field.variable_name?.trim() || 
         field.label.toLowerCase().replace(/[^a-z0-9]/g, '_') || 
         'param';
};

/**
 * Extract media items from FAL result using dot notation path
 */
export const extractMediaFromFalResult = (output: any, outputKey: string): any[] => {
  console.log(`🔍 [EXTRACT] Extracting media from FAL result`);
  console.log(`🔍 [EXTRACT] Output type:`, typeof output);
  console.log(`🔍 [EXTRACT] Is array:`, Array.isArray(output));
  console.log(`🔍 [EXTRACT] Output keys:`, output && typeof output === 'object' ? Object.keys(output) : 'not object');
  console.log(`🔍 [EXTRACT] Output keys available:`, Object.keys(output || {}));
  console.log(`🔍 [EXTRACT] Looking for output key:`, outputKey);
  
  // Additional debug info for multiple variable functionality
  if (output && typeof output === 'object') {
    console.log(`🔍 [EXTRACT] Full output structure preview:`, JSON.stringify(output, null, 2).slice(0, 500) + '...');
  }
  
  try {
    // For FAL streaming, the output is already the root level, no need to access .output
    let current = output;
    
    // Handle dot notation (e.g., "video.url", "audio.url")
    const pathParts = outputKey.split('.');
    console.log(`🔍 [EXTRACT] Path parts:`, pathParts);
    
    // Navigate through the path - start from root since FAL streaming returns output directly
    for (const [index, part] of pathParts.entries()) {
      console.log(`🔍 [EXTRACT] Step ${index + 1}: Looking for "${part}"`);
      console.log(`🔍 [EXTRACT] Current object type:`, typeof current);
      console.log(`🔍 [EXTRACT] Current object keys:`, current && typeof current === 'object' && !Array.isArray(current) ? Object.keys(current) : Array.isArray(current) ? `Array(${current.length})` : 'not object');
      
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
        console.log(`✅ [EXTRACT] Found "${part}":`, Array.isArray(current) ? `Array(${current.length})` : typeof current === 'object' ? 'Object' : current);
      } else {
        console.warn(`⚠️ [EXTRACT] Path part "${part}" not found`);
        console.warn(`🔍 [EXTRACT] Available keys:`, current && typeof current === 'object' ? Object.keys(current) : 'not an object');
        
        // Try alternative approaches before giving up
        if (index === 0) {
          // If first part fails, try looking directly for common output keys
          const commonKeys = ['images', 'image', 'videos', 'video', 'audios', 'audio'];
          for (const commonKey of commonKeys) {
            if (output && typeof output === 'object' && commonKey in output) {
              console.log(`🔄 [EXTRACT] Trying fallback key: ${commonKey}`);
              current = output[commonKey];
              break;
            }
          }
          
          if (!current || (current === output)) {
            return [];
          }
        } else {
          return [];
        }
      }
    }

    console.log(`🎯 [EXTRACT] Final extracted value:`, current);
    
    // Handle different result types
    if (typeof current === 'string') {
      // Direct URL string
      console.log('📎 [EXTRACT] Found direct URL string:', current.slice(-50));
      return [{ url: current }];
    } else if (Array.isArray(current)) {
      // Array of media objects or URLs
      console.log(`📎 [EXTRACT] Found array with ${current.length} items:`, current.map(item => 
        typeof item === 'string' ? item.slice(-30) : (item?.url ? item.url.slice(-30) : 'no url')
      ));
      return current.map(item => 
        typeof item === 'string' 
          ? { url: item }
          : item
      );
    } else if (current && typeof current === 'object' && current.url) {
      // Single media object with URL
      console.log('📎 [EXTRACT] Found single media object with URL:', current.url.slice(-50));
      return [current];
    } else {
      console.warn('⚠️ [EXTRACT] Unexpected media format:');
      console.warn('🔍 [EXTRACT] Type:', typeof current);
      console.warn('🔍 [EXTRACT] Value:', current);
      console.warn('🔍 [EXTRACT] Is array:', Array.isArray(current));
      console.warn('🔍 [EXTRACT] Has url property:', current && typeof current === 'object' && 'url' in current);
      return [];
    }
  } catch (error) {
    console.error('💥 [EXTRACT] Error extracting media:', error);
    console.error('💥 [EXTRACT] Output that caused error:', output);
    console.error('💥 [EXTRACT] Output key that caused error:', outputKey);
    return [];
  }
};

/**
 * Get file extension from output key
 */
export const getFileExtensionFromOutputKey = (key: string): string => {
  const keyLower = key.toLowerCase();
  if (keyLower.includes('video')) return 'mp4';
  if (keyLower.includes('audio') || keyLower.includes('music')) return 'mp3';
  return 'jpg';
};

/**
 * Get output type from key
 */
export const getOutputTypeFromKey = (key: string): 'image' | 'video' | 'audio' => {
  const keyLower = key.toLowerCase();
  if (keyLower.includes('video')) return 'video';
  if (keyLower.includes('audio') || keyLower.includes('music')) return 'audio';
  return 'image';
};

/**
 * Get asset media type from asset object
 */
export const getAssetMediaType = (asset: any): 'image' | 'video' | 'audio' => {
  if (asset.media_type) return asset.media_type;
  
  const extension = asset.file_extension?.toLowerCase() || asset.public_url?.split('.').pop()?.toLowerCase() || '';
  
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension)) return 'audio';
  return 'image';
};